'use client';

import type { KeyboardEvent, Ref } from 'react';
import { ArrowUpRight, Mic, Plus, SlidersHorizontal } from 'lucide-react';

import { MHButton } from '../buttons/MHButton';
import { MHTextarea } from '../forms/MHTextarea';

interface MHChatComposerProps {
  value: string;
  placeholder: string;
  ariaLabel: string;
  onChange: (value: string) => void;
  onSend: () => void;
  inputRef?: Ref<HTMLTextAreaElement>;
  disabled?: boolean;
}

export function MHChatComposer({
  value,
  placeholder,
  ariaLabel,
  onChange,
  onSend,
  inputRef,
  disabled = false,
}: MHChatComposerProps) {
  const hasText = value.trim().length > 0;

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!disabled && hasText) {
        onSend();
      }
    }
  };

  return (
    <section className="pointer-events-none fixed inset-x-0 bottom-0 z-20 px-4 pb-[max(calc(env(safe-area-inset-bottom)+12px),16px)] pt-2 sm:px-6">
      <div className="pointer-events-auto mx-auto w-full max-w-[560px] rounded-[28px] bg-surface-2 p-3">
        <MHTextarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          ref={inputRef}
          rows={1}
          disabled={disabled}
          className="min-h-[44px] resize-none rounded-[22px] bg-transparent px-0 py-2"
        />

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MHButton type="button" variant="mobileComposerIcon" size="icon" aria-label="Add">
              <Plus className="h-5 w-5" />
            </MHButton>
            <MHButton type="button" variant="mobileComposerIcon" size="icon" aria-label="Controls">
              <SlidersHorizontal className="h-5 w-5" />
            </MHButton>
          </div>

          <div className="flex items-center gap-2">
            {hasText ? (
              <MHButton
                type="button"
                variant="mobileComposerSend"
                size="icon"
                aria-label="Send"
                disabled={disabled || !hasText}
                onClick={onSend}
                className="h-11 w-11 rounded-full"
              >
                <ArrowUpRight className="h-5 w-5" />
              </MHButton>
            ) : (
              <MHButton type="button" variant="mobileComposerIcon" size="icon" aria-label="Voice input">
                <Mic className="h-5 w-5" />
              </MHButton>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
